import React, { useState, useEffect } from 'react';
import { 
  Users,
  ShoppingCart,
  TrendingUp,
  Star,
  MessageSquare,
  Loader2,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  BarChart3,
  PieChart
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

export default function CustomerInsightsWidget({ showHeader = true }) {
  const [insights, setInsights] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const data = await apiRequest('/sales/customer-insights');
      setInsights(data || {});
    } catch (error) {
      console.error('Failed to fetch insights:', error);
      setInsights(getMockInsights());
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex flex-col" data-testid="customer-insights-widget">
      {showHeader && (
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Users size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Customer Insights</h3>
              <p className="text-xs text-slate-500">Trends & Feedback</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchInsights}>
            <RefreshCw size={16} />
          </Button>
        </div>
      )}

      {/* Top Categories */}
      <div className="p-4 border-b border-slate-100">
        <p className="text-xs font-medium text-slate-500 mb-3">TOP SELLING CATEGORIES</p>
        <div className="space-y-2">
          {(insights.top_categories || []).map((cat, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                {idx + 1}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-900">{cat.name}</span>
                  <span className="text-sm text-slate-600">{cat.percentage}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                    style={{ width: `${cat.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Customer Sentiment */}
      <div className="p-4 border-b border-slate-100">
        <p className="text-xs font-medium text-slate-500 mb-3">CUSTOMER SENTIMENT</p>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <ThumbsUp size={20} className="mx-auto text-green-500 mb-1" />
            <p className="text-lg font-bold text-green-600">{insights.sentiment?.positive || 0}%</p>
            <p className="text-xs text-slate-500">Positive</p>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <MessageSquare size={20} className="mx-auto text-slate-400 mb-1" />
            <p className="text-lg font-bold text-slate-600">{insights.sentiment?.neutral || 0}%</p>
            <p className="text-xs text-slate-500">Neutral</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <ThumbsDown size={20} className="mx-auto text-red-500 mb-1" />
            <p className="text-lg font-bold text-red-600">{insights.sentiment?.negative || 0}%</p>
            <p className="text-xs text-slate-500">Negative</p>
          </div>
        </div>
      </div>

      {/* Recent Feedback */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-xs font-medium text-slate-500 mb-3">RECENT FEEDBACK</p>
        <div className="space-y-2">
          {(insights.recent_feedback || []).map((feedback, idx) => (
            <div key={idx} className="p-3 rounded-lg bg-slate-50">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-slate-900">{feedback.store}</span>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star 
                      key={i} 
                      size={12} 
                      className={i < feedback.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'} 
                    />
                  ))}
                </div>
              </div>
              <p className="text-xs text-slate-600 line-clamp-2">{feedback.comment}</p>
              <p className="text-xs text-slate-400 mt-1">{feedback.date}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function getMockInsights() {
  return {
    top_categories: [
      { name: 'Dairy & Milk', percentage: 28 },
      { name: 'Spices & Masala', percentage: 22 },
      { name: 'Snacks & Namkeen', percentage: 18 },
      { name: 'Rice & Flour', percentage: 15 },
      { name: 'Fresh Produce', percentage: 12 }
    ],
    sentiment: { positive: 72, neutral: 20, negative: 8 },
    recent_feedback: [
      { store: 'Clyde', rating: 5, comment: 'Great selection of Indian groceries! Love the fresh vegetables.', date: '2 hours ago' },
      { store: 'Craigieburn', rating: 4, comment: 'Good prices but sometimes items are out of stock.', date: '5 hours ago' },
      { store: 'Hoppers', rating: 5, comment: 'Best paneer in Melbourne! Staff is very helpful.', date: '1 day ago' },
      { store: 'Melton', rating: 3, comment: 'Decent store but checkout lines can be long on weekends.', date: '2 days ago' }
    ]
  };
}
